'use client'

import Typography from '@mui/material/Typography'
import Image, { getImageProps, StaticImageData } from 'next/image'
import { FC, PropsWithChildren, ReactNode } from 'react'
import GridContainer from '../GridContainer'

interface HeroSectionProps extends PropsWithChildren {
  title: ReactNode
  imageSrc: StaticImageData
  imageAltText: string
  mobileImageSrc?: StaticImageData
}

const HERO_DESKTOP_MEDIA = '(min-width: 768px)'
const HERO_IMAGE_SIZES = '(min-width: 1024px) 50vw, 100vw'

const HeroSection: FC<HeroSectionProps> = ({
  title,
  imageSrc,
  imageAltText,
  mobileImageSrc,
  children,
}) => {
  const renderHeroImage = () => {
    if (!mobileImageSrc) {
      return (
        <Image
          src={imageSrc}
          alt={imageAltText}
          priority
          fill
          sizes={HERO_IMAGE_SIZES}
          className="object-cover"
        />
      )
    }

    const common = {
      alt: imageAltText,
      priority: true,
      fill: true,
      sizes: HERO_IMAGE_SIZES,
    }
    const {
      props: { srcSet: desktopSrcSet, sizes: desktopSizes },
    } = getImageProps({ ...common, src: imageSrc })
    const {
      props: { srcSet: mobileSrcSet, sizes: mobileSizes, ...imgProps },
    } = getImageProps({ ...common, src: mobileImageSrc })

    return (
      <picture>
        <source media={HERO_DESKTOP_MEDIA} srcSet={desktopSrcSet} sizes={desktopSizes} />
        <source srcSet={mobileSrcSet} sizes={mobileSizes} />
        <img {...imgProps} alt={imageAltText} className="object-cover" />
      </picture>
    )
  }

  return (
    <div className="bg-ink-100 py-16 lg:py-24 lg:max-h-[80vh] lg:flex lg:items-center">
      <GridContainer>
        <div
          className={`
            col-span-full lg:col-span-5
            lg:flex flex-col gap-6 lg:justify-center
            mb-6 lg:mb-0
            ${children ? 'hidden' : ''}
          `}
        >
          <Typography variant="h1">{title}</Typography>
          <div className="hidden lg:block">{children}</div>
        </div>
        <div
          className={`
            lg:col-start-7 col-span-full lg:col-span-6
            flex flex-col gap-6
          `}
        >
          <div
            className={`
              relative aspect-[1]
              lg:max-h-[calc(80vh-6rem)]
              rounded-[12px] lg:rounded-[16px]
              overflow-hidden
            `}
          >
            {renderHeroImage()}
          </div>
          {children && (
            <div className="lg:hidden flex flex-col gap-6">
              <Typography variant="h1">{title}</Typography>
              {children}
            </div>
          )}
        </div>
      </GridContainer>
    </div>
  )
}

export default HeroSection
